import { inject, Injectable } from '@angular/core';
import { Bookmark } from '../models/bookmark';
import {
  BookmarkValidationIssue,
  BookmarkValidationResult,
  BookmarkValidationSummary,
} from '../models/bookmark-validation';
import { CustomGridStackWidget } from './grid-widget-config.service';
import { IndicatorDataStore } from '../stores/indicatpr-data/indicator-data.state';
import { DataLayerStore } from '../stores/data-layer/data-layer.state';
import { IndicatorStore } from '../stores/indicator/indicator.state';
import { IndicatorData } from '../models/indicator-data';
import { DataLayer } from '../models/data-layer';
import { Indicator } from '../models/indicator';

@Injectable({
  providedIn: 'root',
})
export class BookmarkValidationService {
  private readonly indicatorDataStore = inject(IndicatorDataStore);
  private readonly dataLayerStore = inject(DataLayerStore);
  private readonly indicatorStore = inject(IndicatorStore);

  /**
   * Validates a single bookmark for publication issues
   * @param bookmark The bookmark to validate
   * @returns Validation result with all found issues
   */
  validateBookmark(bookmark: Bookmark): BookmarkValidationResult {
    const issues: BookmarkValidationIssue[] = [];

    // Only validate public bookmarks (private bookmarks don't need validation)
    if (bookmark.private || !bookmark.published) {
      return {
        bookmarkId: bookmark._id,
        bookmarkTitle: bookmark.title,
        isValid: true,
        issues: [],
        validatedAt: new Date(),
      };
    }

    // Check each widget in the bookmark configuration
    if (bookmark.config && Array.isArray(bookmark.config)) {
      bookmark.config.forEach((widget: CustomGridStackWidget) => {
        const widgetIssues = this.validateWidget(widget, bookmark);
        issues.push(...widgetIssues);
      });
    }

    return {
      bookmarkId: bookmark._id,
      bookmarkTitle: bookmark.title,
      isValid: issues.length === 0,
      issues,
      validatedAt: new Date(),
    };
  }

  /**
   * Validates a single widget for publication issues
   * @param widget The widget to validate
   * @param bookmark The parent bookmark
   * @returns Array of validation issues found
   */
  private validateWidget(
    widget: CustomGridStackWidget,
    bookmark: Bookmark
  ): BookmarkValidationIssue[] {
    const issues: BookmarkValidationIssue[] = [];

    // Skip content widgets (text, teaser, info)
    if (!widget.widget || ['text', 'teaser', 'info'].includes(widget.type)) {
      return issues;
    }

    const widgetConfig = widget.widget;

    // Get all data layers from widget
    const dataLayerIds = this.getDataLayerIdsFromWidget(widgetConfig);

    // Get all indicators from widget
    const indicatorIds = this.getIndicatorIdsFromWidget(widgetConfig);

    // Check each combination of dataLayer and indicator
    dataLayerIds.forEach(dataLayerId => {
      const dataLayer = this.dataLayerStore.getById(dataLayerId);

      if (!dataLayer) {
        // DataLayer not found - create missing-datalayer issue
        issues.push(
          this.createIssue(
            bookmark,
            widget,
            'missing-datalayer',
            undefined,
            undefined,
            undefined,
            dataLayerId
          )
        );
        return; // Skip further checks for this dataLayer
      }

      // Check if dataLayer is published
      if (!dataLayer.published) {
        issues.push(
          this.createIssue(
            bookmark,
            widget,
            'unpublished-datalayer',
            dataLayer,
            undefined,
            undefined
          )
        );
      }

      indicatorIds.forEach(indicatorId => {
        const indicator = this.indicatorStore.getById(indicatorId);

        if (!indicator) {
          // Indicator not found - create missing-indicator issue
          issues.push(
            this.createIssue(
              bookmark,
              widget,
              'missing-indicator',
              dataLayer,
              undefined,
              undefined,
              indicatorId
            )
          );
          return; // Skip further checks for this indicator
        }

        // Find the IndicatorData for this combination
        const indicatorData =
          this.indicatorDataStore.getByDataLayerAndIndicator(
            dataLayerId,
            indicatorId
          );

        if (indicatorData) {
          // Check if IndicatorData is published
          if (!indicatorData.published) {
            issues.push(
              this.createIssue(
                bookmark,
                widget,
                'unpublished-data',
                dataLayer,
                indicator,
                indicatorData
              )
            );
          }
        }
      });
    });

    return issues;
  }

  /**
   * Extracts all dataLayer IDs from a widget configuration
   * @param widgetConfig The widget configuration
   * @returns Array of dataLayer IDs
   */
  private getDataLayerIdsFromWidget(widgetConfig: any): string[] {
    const ids: string[] = [];

    // Single dataLayer
    if (widgetConfig.dataLayer) {
      ids.push(widgetConfig.dataLayer);
    }

    // Multiple dataLayers
    if (
      widgetConfig.dataLayers &&
      Array.isArray(widgetConfig.dataLayers) &&
      widgetConfig.dataLayers.length > 0
    ) {
      ids.push(...widgetConfig.dataLayers);
    }

    return [...new Set(ids)]; // Remove duplicates
  }

  /**
   * Extracts all indicator IDs from a widget configuration
   * @param widgetConfig The widget configuration
   * @returns Array of indicator IDs
   */
  private getIndicatorIdsFromWidget(widgetConfig: any): string[] {
    const ids: string[] = [];

    // Single indicator
    if (widgetConfig.indicator) {
      ids.push(widgetConfig.indicator);
    }

    // Multiple indicators
    if (
      widgetConfig.indicators &&
      Array.isArray(widgetConfig.indicators) &&
      widgetConfig.indicators.length > 0
    ) {
      ids.push(...widgetConfig.indicators);
    }

    // Axis properties for scatter/bubble charts
    if (
      widgetConfig.axisProperties &&
      Array.isArray(widgetConfig.axisProperties)
    ) {
      ids.push(...widgetConfig.axisProperties);
    }

    return [...new Set(ids)]; // Remove duplicates
  }

  /**
   * Creates a validation issue object
   */
  private createIssue(
    bookmark: Bookmark,
    widget: CustomGridStackWidget,
    issueType: BookmarkValidationIssue['issueType'],
    dataLayer?: DataLayer,
    indicator?: Indicator,
    indicatorData?: IndicatorData,
    missingId?: string
  ): BookmarkValidationIssue {
    let message = '';

    switch (issueType) {
      case 'unpublished-datalayer':
        message = `Jahr "${dataLayer?.year}" ist nicht veröffentlicht`;
        break;
      case 'unpublished-indicator':
        message = `Indikator "${indicator?.name}" ist nicht veröffentlicht`;
        break;
      case 'unpublished-data':
        message = `Daten für "${indicator?.name}" in "${dataLayer?.year}" sind nicht veröffentlicht`;
        break;
      case 'missing-datalayer':
        message = `Jahr mit ID "${missingId}" wurde nicht gefunden`;
        break;
      case 'missing-indicator':
        message = `Indikator mit ID "${missingId}" wurde nicht gefunden`;
        break;
    }

    // Generate widget title
    const widgetTitle = this.getWidgetTitle(widget, indicator);

    return {
      bookmarkId: bookmark._id,
      bookmarkTitle: bookmark.title,
      issueType,
      widgetId: String(widget.id),
      widgetType: widget.type,
      widgetHeadline: widgetTitle,
      details: {
        dataLayerId: dataLayer?._id || missingId,
        dataLayerName: dataLayer?.year ? String(dataLayer.year) : missingId,
        indicatorId: indicator?._id || missingId,
        indicatorName: indicator?.name || missingId,
        indicatorDataId: indicatorData?._id,
      },
      message,
    };
  }

  /**
   * Gets a descriptive title for a widget
   * Priority: 1. Custom headline, 2. Indicator name, 3. Widget type
   */
  private getWidgetTitle(
    widget: CustomGridStackWidget,
    indicator?: Indicator
  ): string {
    // First priority: custom headline
    if (widget.widget?.headline) {
      return widget.widget.headline;
    }

    // Second priority: indicator name
    if (indicator?.name) {
      return indicator.name;
    }

    // Third priority: widget type with multiple indicators
    if (widget.widget?.indicators && widget.widget.indicators.length > 0) {
      const firstIndicator = this.indicatorStore.getById(
        widget.widget.indicators[0]
      );
      if (firstIndicator) {
        return `${firstIndicator.name} (+${widget.widget.indicators.length - 1})`;
      }
    }

    // Fallback: widget type label
    return this.getWidgetTypeLabel(widget.type);
  }

  /**
   * Gets a human-readable label for widget type
   */
  private getWidgetTypeLabel(type: string): string {
    const labels: { [key: string]: string } = {
      map: 'Karte',
      bar: 'Balken-/Säulendiagramm',
      line: 'Liniendiagramm',
      pie: 'Kreis-/Ringdiagramm',
      scatter: 'Streudiagramm',
      bubble: 'Blasendiagramm',
      radar: 'Netz-/Radardiagramm',
      polarArea: 'Polardiagramm',
      table: 'Tabelle',
      text: 'Text',
      info: 'Indikatorinfo',
      teaser: 'Teaser/Bild',
    };

    return labels[type] || type;
  }

  /**
   * Validates multiple bookmarks
   * @param bookmarks Array of bookmarks to validate
   * @returns Summary of validation results
   */
  validateBookmarks(bookmarks: Bookmark[]): BookmarkValidationSummary {
    const results = bookmarks.map(bookmark => this.validateBookmark(bookmark));

    const validBookmarks = results.filter(r => r.isValid).length;
    const invalidBookmarks = results.filter(r => !r.isValid).length;
    const totalIssues = results.reduce((sum, r) => sum + r.issues.length, 0);

    return {
      totalBookmarks: bookmarks.length,
      validBookmarks,
      invalidBookmarks,
      totalIssues,
      results,
      validatedAt: new Date(),
    };
  }

  /**
   * Validates only public bookmarks
   * @param bookmarks Array of all bookmarks
   * @returns Summary of validation results for public bookmarks only
   */
  validatePublicBookmarks(bookmarks: Bookmark[]): BookmarkValidationSummary {
    const publicBookmarks = bookmarks.filter(b => !b.private && b.published);
    return this.validateBookmarks(publicBookmarks);
  }
}
